import { installInlineActions } from '@presentation/shell/InlineActions';
import { NexxusShell } from '@presentation/shell/NexxusShell';
import { viewControllers } from '@presentation/shell/controllers';
import '@presentation/components/StatusBadge';
import '@presentation/components/Modal';
import { installPasswordVisibility } from '@presentation/components/PasswordVisibility';
import { ToastService } from '@presentation/components/Toast';
import { logger } from '@infra/telemetry/Logger';
import { AppContext } from '@application/context/AppContext';
import { bootstrapLicence } from '@application/licence/LicenseBootstrap';
import { TenantProvisioningService } from '@application/licence/TenantProvisioningService';
import { migrateV22ToV23 } from '@infra/persistence/Migrations/v22_to_v23';
import { DexieToSqliteCutoverService } from '@infra/persistence/Migrations/DexieToSqliteCutoverService';
import { RemoteSecureCacheBootstrap } from '@infra/persistence/Migrations/RemoteSecureCacheBootstrap';
import { isLocalOfflineMode, isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { resolveSignedDataMode } from '@infra/licence/DataModeResolver';
import { HealthCheckService } from '@infra/health/HealthCheckService';
import { canLoadDemoSeed, isDemoSeedOptIn, setDemoSeedOptIn } from '@infra/licence/demoSeedConfig';
import { migrateModuleDataFromLocalStorage } from '@infra/persistence/Migrations/ModuleDataMigrationService';
import { scopeLocalTablesToActiveTenant } from '@application/licence/ActiveTenant';
import { bindSyncReplayToActiveTenant } from '@application/persistence/ActiveTenantSyncReplay';
import { CoffreFortStorageService } from '@application/persistence/CoffreFortStorageService';
import { syncEntitlementsWhenOnline } from '@application/licence/LicenceEntitlementSync';
import {
  applyCachedEntitlementsFromTenant,
  syncLicenceEntitlements,
} from '@application/licence/LicenceEntitlementService';
import { performAuthLogout } from '@application/auth/authBridge';
import {
  expiredLicenceCopy,
  isLicenceExpiredError,
  openLicenceSubscribePage,
} from '@application/licence/LicenceExpiryCopy';
import { resolveNeedsFirstAdmin } from '@application/auth/FirstAdminSetup';
import { RemoteAuthService } from '@application/auth/RemoteAuthService';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import {
  clearCloudLicenceGate,
  showCloudLicenceLoading,
  showCloudLicenceMissing,
} from '@presentation/shell/CloudLicenceGateUi';
import { enforceCguGate } from '@application/legal/CguGate';
import { initIssuerInfo, refreshIssuerInfo } from '@infra/legal/IssuerInfoService';
import { LicensingSessionService } from '@infra/licence/LicensingSessionService';
import type { Tenant } from '@shared/types/Tenant';
import { getAppVersion } from '@shared/constants/appVersion';
import {
  countUsersForTenant,
  isSessionTenantMismatch,
  licenceRejectionMessage,
} from '@application/licence/LicenceAccountGuard';
import { ensureDocumentLang } from '@shared/a11y/ensureDocumentLang';
import { renderStartupFailureScreen } from '@presentation/shell/StartupFailureScreen';
import { startCloudWebVersionWatcher } from '@application/update/CloudWebVersionWatcher';

function renderStartupShell(root: HTMLElement): void {
  root.className = '';
  root.innerHTML = `
    <div class="login-page" id="startup-shell">
      <div class="login-card">
        <p style="text-align:center; color:var(--gris); margin:0;">Chargement de Nexxus ERP…</p>
      </div>
    </div>`;
}

function isLicenseGateVisible(root: HTMLElement): boolean {
  return Boolean(root.querySelector('#license-gate-input'));
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`Délai dépassé pendant « ${label} ».`));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

async function bootstrap(): Promise<void> {
  // CSP stricte : les actions des gabarits (data-on-*) sont traitées par un écouteur délégué, jamais par du code en ligne.
  installInlineActions();
  // Phase4 G1 / WCAG-B-03 — langue FR pour lecteurs d’écran (index.html + repli runtime).
  ensureDocumentLang('fr');
  logger.info({ event: 'app_starting', version: getAppVersion() });

  const root = document.getElementById('app');
  if (!root) {
    logger.error({ event: 'no_root_element' });
    return;
  }

  renderStartupShell(root);
  let step = 'ouverture';
  const watchdog = window.setTimeout(() => {
    if (!root.querySelector('#startup-shell') || isLicenseGateVisible(root)) return;
    logger.error({ event: 'bootstrap_watchdog', step });
    const appRoot = document.getElementById('app');
    if (!appRoot) return;
    void renderStartupFailureScreen(
      appRoot,
      new Error(`Le démarrage reste bloqué à l’étape « ${step} ».`),
    );
  }, 20_000);
  const stopWatchdog = (): void => window.clearTimeout(watchdog);

  const ctx = AppContext.getInstance();
  step = 'base locale';
  await withTimeout(ctx.init(), 8_000, 'base locale');

  // Informations de l'émetteur (écran d'activation, CGU, pieds de page) : copie signée en cache, puis rafraîchie
  // en ligne sans bloquer le démarrage.
  await initIssuerInfo();
  void refreshIssuerInfo();

  // 1. Validation locale du certificat (sans attente réseau bloquante)
  try {
    step = 'licence';
    await bootstrapLicence(root, ctx);
  } catch (err) {
    stopWatchdog();
    if (isLicenseGateVisible(root)) return;
    throw err;
  }

  // 2. Persistance locale chiffrée (SQLite SQLCipher)
  const signedMode = resolveSignedDataMode(ctx.getLicenceRuntime()?.getState()?.claims);
  if (isLocalOfflineMode(signedMode)) {
    const tenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
    const cutover = await DexieToSqliteCutoverService.maybeMigrate(ctx.db, signedMode, tenantId);
    if (!cutover.skipped && !cutover.migrated) {
      ToastService.warning(
        `Persistance locale dégradée (SQLite non activé) : ${cutover.reason ?? 'cause inconnue'}. Les saisies restent disponibles via le stockage de secours.`,
      );
      logger.warn({ event: 'sqlite_cutover_failed', reason: cutover.reason });
    }
  } else if (isRemoteMultiStationMode(signedMode)) {
    await RemoteSecureCacheBootstrap.ensure(ctx);
  }

  // Cloud navigateur : la session vit dans le cookie httpOnly. Avant de lier les dépôts à une entreprise, on relit
  // GET /v1/auth/me : entreprise, rôle et droits RÉELS de la licence (sinon retour à l'écran de connexion).
  // Desktop / Electron : ce bloc est entièrement ignoré (guard isBrowserCloudHost).
  if (isBrowserCloudHost()) {
    const cloudRuntime = ctx.getLicenceRuntime();
    // Session locale + lecture seule sans module = /me n'a pas encore livré les droits.
    if (
      ctx.auth.getSession() &&
      cloudRuntime?.isReadOnly() &&
      cloudRuntime.getEntitledModuleCodes().length === 0
    ) {
      showCloudLicenceLoading(root);
    }
    step = 'session';
    await new RemoteAuthService().refreshSessionFromServer();
  }

  // 3. Résolution du mode signé + adapter local ou distant
  await ctx.configurePersistenceFromLicence();

  const activeTenantId =
    ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
    ctx.tenantContext.tryGetTenant()?.id ??
    'default';
  scopeLocalTablesToActiveTenant(ctx);
  await migrateModuleDataFromLocalStorage(activeTenantId);

  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    bindSyncReplayToActiveTenant(ctx);
  }

  // 3. Provisioning tenant local depuis les claims
  try {
    await TenantProvisioningService.ensureTenantFromLicence(ctx);
  } catch (err) {
    if (!isLicenceExpiredError(err)) throw err;
    logger.warn({ event: 'tenant_provision_skipped_expired' });
  }

  const licenceRuntime = ctx.getLicenceRuntime();
  if (licenceRuntime?.getState().valid) {
    await applyCachedEntitlementsFromTenant(ctx, licenceRuntime);
    if (!licenceRuntime.isReadOnly()) {
      await syncLicenceEntitlements(ctx, licenceRuntime);
    }
    void syncEntitlementsWhenOnline(ctx);
  }

  const licenceClaims = licenceRuntime?.getState()?.claims;
  const licenceTenantId = licenceClaims?.tenant_id;

  // Session résiduelle (autre tenant / ancienne démo) → déconnexion
  let session = ctx.auth.getSession();
  if (isSessionTenantMismatch(session?.tenant_id, licenceTenantId)) {
    ctx.auth.logout();
    session = null;
    ToastService.warning(
      'La licence activée correspond à une autre entité. Créez le premier administrateur ou connectez-vous avec un compte de cette entité.',
    );
  }

  const usersForLicenceTenant = licenceTenantId
    ? await countUsersForTenant(ctx, licenceTenantId)
    : 0;
  const needsFirstAdmin = await resolveNeedsFirstAdmin(ctx, licenceTenantId);

  // Seed démo auto (tenant démo / env) — l’opt-in activation est traité après login
  if (
    canLoadDemoSeed() &&
    !isDemoSeedOptIn() &&
    usersForLicenceTenant === 0 &&
    (!licenceTenantId || TenantProvisioningService.isDemoTenant(licenceTenantId))
  ) {
    const { SeedDemoData } = await import('@infra/seed/SeedDemoData');
    const res = await SeedDemoData.seed(ctx);
    await ctx.localTables.put('tenants', res.tenant);
    await ctx.auth.register(res.tenant.id, 'rh@demo.nexxus.ga', 'demo1234', 'Démo', 'RH', 'rh');
    await ctx.auth.register(
      res.tenant.id,
      'compta@demo.nexxus.ga',
      'demo1234',
      'Démo',
      'Compta',
      'comptable',
    );
    logger.info({
      event: 'seed_demo_done',
      salaries: res.salaries_count,
      bulletins: res.bulletins_count,
    });
    ToastService.success('Données de démonstration chargées');
  }

  const migrated = await migrateV22ToV23(ctx.db);
  if (migrated) {
    logger.info({ event: 'migration_complete' });
    ToastService.success('Migration v2.2 → v2.3 réussie');
  }

  const health = new HealthCheckService();
  health.register('database', async () => ({ status: 'ok' }));
  const report = await health.run();
  logger.info({ event: 'healthcheck', global: report.global });

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (sentryDsn) {
    const { SentryAdapter } = await import('@infra/monitoring/SentryAdapter');
    const sentry = new SentryAdapter({
      dsn: sentryDsn,
      environment: import.meta.env.PROD ? 'production' : 'development',
      release: getAppVersion(),
    });
    await sentry.init();
    sentry.installGlobalHandlers();
  }

  // Pas de contrôle de version dans le navigateur Cloud : le site est toujours servi à jour.
  if (
    !isBrowserCloudHost() &&
    (import.meta.env.PROD || import.meta.env.VITE_ENABLE_VERSION_CHECK === 'true')
  ) {
    const { VersionChecker } = await import('@infra/update/VersionChecker');
    const currentVersion = (await window.electron?.getVersion?.()) ?? getAppVersion();
    const checker = new VersionChecker(currentVersion);
    const checkVersion = () => {
      if (!checker.shouldCheck()) return;
      void checker.check().then((info) => {
        if (info?.isOutdated) {
          logger.info({ event: 'update_available', latest: info.latest });
          ToastService.info(`Mise à jour ${info.latest} disponible — voir Paramètres`);
        }
      });
    };
    checkVersion();
    window.setInterval(checkVersion, 60 * 60 * 1000);
  }

  if (needsFirstAdmin) {
    stopWatchdog();
    await performAuthLogout(ctx);
    NexxusShell.renderFirstAdminSetup(root, ctx, licenceTenantId!, () => location.reload());
    return;
  }

  if (!session) {
    stopWatchdog();
    if (isBrowserCloudHost()) clearCloudLicenceGate(root);
    NexxusShell.renderLogin(root, ctx, () => location.reload());
    return;
  }

  // Cloud post-login : après /me (+ entitlements appliqués), aucun module → porte licence.
  // L'écran login n'est jamais remplacé ici (session absente déjà traitée ci-dessus).
  if (isBrowserCloudHost()) {
    const entitled = ctx.getLicenceRuntime()?.getEntitledModuleCodes() ?? [];
    if (entitled.length === 0) {
      stopWatchdog();
      showCloudLicenceMissing(root);
      return;
    }
    clearCloudLicenceGate(root);
  }

  const tenants = await ctx.localTables.list<Tenant>('tenants');
  const userTenant = tenants.find((t) => t.id === session.tenant_id);
  if (userTenant) ctx.tenantContext.setTenant(userTenant);

  const salariesCount = await ctx.localTables.countByTenant('salaries', session.tenant_id);
  const licence = ctx.getLicenceRuntime();
  if (licence) {
    const licState = await licence.revalidateWithTenant(session.tenant_id, salariesCount);
    if (!licState.valid) {
      ToastService.error(licenceRejectionMessage(licState.errorCode));
      await performAuthLogout(ctx);
      if (
        licState.errorCode === 'TENANT_MISMATCH' &&
        licenceTenantId &&
        !TenantProvisioningService.isDemoTenant(licenceTenantId) &&
        (await resolveNeedsFirstAdmin(ctx, licenceTenantId))
      ) {
        stopWatchdog();
        NexxusShell.renderFirstAdminSetup(root, ctx, licenceTenantId, () => location.reload());
        return;
      }
      stopWatchdog();
      NexxusShell.renderLogin(root, ctx, () => location.reload());
      return;
    }
    if (licState.isReadOnly) {
      const restriction = licence.getRestriction();
      const copy = expiredLicenceCopy(Boolean(licence.getState().claims?.trial));
      ToastService.warning(restriction ? restriction.message : copy.body);
    }
  }

  // Session installation (licensing-api) en mode distant
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const ok = await LicensingSessionService.ensureInstallationSession();
    if (!ok) {
      ToastService.warning('Serveur de licence ou session installation indisponible');
    }
  }

  stopWatchdog();
  const trial = Boolean(ctx.getLicenceRuntime()?.getState()?.claims?.trial);
  const cguOk = await enforceCguGate(root, { userId: session.user_id, isTrial: trial });
  if (!cguOk) return;

  const shell = new NexxusShell(root, ctx, session);
  shell.registerAll(viewControllers);
  shell.mount();

  try {
    const { mountHybridBackupUi } = await import(
      '@presentation/views/parametres/hybrid/HybridBackupUiBootstrap'
    );
    await mountHybridBackupUi({ ctx, tenantId: session.tenant_id, root });
  } catch (err) {
    logger.warn({
      event: 'hybrid_backup_ui_mount_failed',
      error: err instanceof Error ? err.message : String(err),
    });
  }

  if (isDemoSeedOptIn() && session.tenant_id) {
    try {
      const count = await ctx.localTables.countByTenant('salaries', session.tenant_id);
      if (count === 0) {
        const { SeedDemoData } = await import('@infra/seed/SeedDemoData');
        const res = await SeedDemoData.seedIntoTenant(ctx, session.tenant_id);
        setDemoSeedOptIn(false);
        ToastService.success(
          `Données démo chargées (${res.salaries_count} salariés, ${res.bulletins_count} bulletins).`,
        );
      } else {
        setDemoSeedOptIn(false);
      }
    } catch (err) {
      logger.warn({
        event: 'demo_seed_opt_in_failed',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    void CoffreFortStorageService.syncPendingUploads(ctx, session.tenant_id);
  }
}

installPasswordVisibility();

function renderStartupFailure(root: HTMLElement, err: unknown): void {
  if (isLicenceExpiredError(err)) {
    const copy = expiredLicenceCopy();
    root.innerHTML = `
      <div class="login-page">
        <div class="login-card">
          <h2 style="margin-top:0;color:var(--marine);">${copy.title}</h2>
          <p style="color:var(--gris);">${copy.body}</p>
          <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:16px;">
            <button type="button" id="startup-subscribe" style="padding:8px 16px;background:var(--teal);color:white;border:none;border-radius:4px;cursor:pointer;">${copy.cta}</button>
            <button type="button" data-on-click="nx_reload()" style="padding:8px 16px;background:transparent;color:var(--marine);border:1px solid var(--teal);border-radius:4px;cursor:pointer;">Continuer en lecture seule</button>
          </div>
        </div>
      </div>`;
    root.querySelector('#startup-subscribe')?.addEventListener('click', () => {
      void openLicenceSubscribePage();
    });
    return;
  }

  void renderStartupFailureScreen(root, err);
}

// Site Cloud : prévient quand une nouvelle version est déployée pendant que l'onglet reste ouvert (écran de connexion inclus).
startCloudWebVersionWatcher();

bootstrap().catch((err: unknown) => {
  logger.error({ event: 'bootstrap_failed', error: String(err), stack: err instanceof Error ? String(err.stack).slice(0, 700) : undefined });
  const root = document.getElementById('app');
  if (root) renderStartupFailure(root, err);
});
